import { Injectable } from '@angular/core';
import { ConsentService } from '../components/global/consent/consent.service';
import { prod_fe_uri } from '../../environments/environment.prod';
import { GtagService } from './gx-gtag/gtag.service';

type Scripts = {
  [url: string]: { tag: HTMLScriptElement; isLoaded: boolean; }
}

@Injectable({
  providedIn: 'root',
})
export class ScriptService {
  private trackedScripts: Scripts = {};

  constructor(private consentService: ConsentService, private gtagService: GtagService) {
  }

  /**
   * Loads map script
   * If map key/language changed, will remove existing map script and helper scripts and reload the
   * map script (reload helper scripts separately)
   * The map script has to be reloaded on language change in order for the lang change to take effect!
   * Any previously created map object/artifact/ev. handler has to be flushed and recreated!
   * @param mapsKey
   * @param language
   */
  loadGoogleMapsScript(mapsKey: string, language: string) {
    return this.loadScript({
      url: `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&language=${language}&libraries=places`,
    });
  }

  /**
   * Load Marker Clusterer Script
   */
   loadMarkerClustererScript() {
     return this.loadScript({
       url: 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js',
     });
   }

  /**
   * Lazy-loads a script and checks if it's not already loaded.
   * @param url
   * @param async
   * @param defer
   * @param forceNewLoad
   */
  loadScript({
               url,
               async = true,
               defer = true,
               // for bypassing the `trackedScripts`
               forceNewLoad = false,
             }: { url: string; async?: boolean; defer?: boolean; forceNewLoad?: boolean }): Promise<{ scriptSrc: string, status: string }> {
    return new Promise((resolve, reject) => {
      const trackedScript = !forceNewLoad && this.trackedScripts[url];

      // 1. Case: Script is already fully loaded
      if (trackedScript?.isLoaded) {
        return resolve({ scriptSrc: url, status: 'Already Loaded' });
      }

      // 2. Case: Script is currently in the middle of loading
      if (trackedScript && !trackedScript.isLoaded) {
        // Use addEventListener so we don't interfere with the first caller's .onload
        // { once: true } ensures the listener is removed automatically after firing
        trackedScript.tag.addEventListener('load', () => {
          resolve({ scriptSrc: url, status: 'Loaded (attached to existing)' });
        }, { once: true });

        trackedScript.tag.addEventListener('error', (error) => {
          reject(error);
        }, { once: true });

        return;
      }

      // 3. Case: New script load required
      if (ScriptService.isGoogleMapsScript(url)) {
        this.removeAllGoogleMapsScripts();
        this.removeGoogleMapsHelperScripts();
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.async = async;
      script.defer = defer;

      // Track it immediately so concurrent calls hit Case 2
      this.trackedScripts[url] = { tag: script, isLoaded: false };

      script.onload = () => {
        if (this.trackedScripts[url]) {
          this.trackedScripts[url].isLoaded = true;
        }
        resolve({ scriptSrc: url, status: 'Loaded' });
      };

      script.onerror = (error) => {
        // Remove from tracking so a retry actually attempts a fresh load
        delete this.trackedScripts[url];
        console.error(`Lazy-loading script ${url} failed.`, error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Removes all Google-Maps-related scripts from HTML and from the `trackedScripts`.
   * Called when logging out in order to delete the Google Maps JavaScript API script that requires
   * a different API-key per user.
   */
  removeAllGoogleMapsScripts() {
    this.removeScripts([ScriptService.isGoogleMapsScript]);

    // Deleting the existing gmaps namespace to prevent memory leaks.
    // There's no official solution for this (see https://stackoverflow.com/q/10485582/8160318)
    // but the following works just fine.
    if (typeof window.google?.maps !== 'undefined') {
      window.google = {} as any;
    }
  }

  /**
   * Called after logging-out. Failing to remove spiderfier/clusterer will cause gmaps errors
   * after subsequent login. Doesn't seem to be necessary to call together w/ lang changes ...
   * Fixes unresponsive map behaviour described in https://gxperts.atlassian.net/browse/BLS-4791
   * Should not be required after user mngmnt goes live as logging out will cause a
   * redirect to auth0 login page.
   */
  removeGoogleMapsHelperScripts() {
    this.removeScripts([
      ScriptService.isSpiderfierScript,
      ScriptService.isClustererScript]);
  }

  /**
   * Loads gtag script, defines a global gtag function and sets default consent values.
   * Should be run just once and as soon as possible.
   * https://developers.google.com/tag-platform/security/guides/consent
   */
  loadGtagScript() {
    const cookie = this.consentService.getConsentCookie();
    const tagId = 'G-5KJZHLHZ78';
    const scriptUrl = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;

    // Define dataLayer
    window['dataLayer'] = window['dataLayer'] || [];
    // Define tag function
    if (prod_fe_uri.includes(location.hostname)) {
      console.log('** GA production **');

      window['gtag'] = function gtag() {
        window['dataLayer'].push(arguments);
      };
    } else {
      console.log('** GA dev or staging **');

      window['gtag'] = function gtag() {
        console.debug('Would\'ve sent the following args to GA but did not because ' +
            'we\'re NOT in production', { ...arguments });
      };
    }

    this.gtagService.setDefaultConsent(cookie);
    this.gtagService.js();
    this.gtagService.config({ 'send_page_view': false });
    this.loadScript({ url: scriptUrl }).then();
  }

  /**
   * Checks if a script is a Google-Maps Script
   * @param src
   */
  private static isGoogleMapsScript(src: string) {
    return src.includes('googleapis.com/maps') ||
        src.includes('maps.gstatic.com');
  }

  /**
   * Checks for Clusterer Script
   * @param src
   */
  private static isClustererScript(src: string) {
    return src.includes('markerclustererplus');
  }

  /**
   * Checks for Spiderfier Script
   * @param src
   */
  private static isSpiderfierScript(src: string) {
    return src.includes('OverlappingMarkerSpiderfier');
  }

  /**
   * Removes scripts from HTML and from the `trackedScripts`
   * @param scriptIdentifiers
   * @private
   */
  private removeScripts(scriptIdentifiers: ((src: string) => boolean)[]) {
    const allScriptsTags = Array.from(document.getElementsByTagName('script'));

    for (const scriptTag of allScriptsTags) {
      const { src } = scriptTag;
      for (const scriptIdentifier of scriptIdentifiers) {
        if (scriptIdentifier(src)) {
          // remove the script tag from html
          scriptTag.remove();

          if (src in this.trackedScripts) {
            // do remove from our loaded-scripts-tracker too
            delete this.trackedScripts[src];
          }
        }
      }
    }
  }
}
